import React, { useState, useEffect, useRef } from 'react';
import { ReactComponent as Upload } from '../../../assets/svg/upload.svg';
import { ReactComponent as Delete } from '../../../assets/svg/delete.svg';
import styled, { css } from 'styled-components';
import { storage } from '../../../firebase';
import { v4 as uuid } from 'uuid';
import { useImages } from '../../../hooks/useImages';

const ImageHandler = ({ image = undefined, onChange = () => { } }) => {
  const [source, setSource] = useState(image);
  const images = useImages([source]);

  const [loading, setLoading] = useState(false);
  const dialogRef = useRef();

  useEffect(() => {
    setSource(image);
  }, [image]);

  useEffect(() => {
    onChange({ name: source });
  }, [source, onChange]);

  const openDialog = () => {
    dialogRef.current.value = '';
    dialogRef.current.click();
  };

  const onSelect = (e) => {
    const files = e.target.files;
    if (files.length === 0) return;
    setLoading(true);
    const file = files[0];
    asyncRefactorImage(file)
      .then((blob) => imageUpload(blob))
      .then((name) => setSource(name))
      .finally(() => setLoading(false));
  };

  return (
    <Container>
      <Wrapper loading={loading}>
        {source && images[source] ? <Img src={images[source]} /> : <NoImage>No image</NoImage>}
        <Options>
          {source && (
            <Option backgroundColor='#b0bec5' onClick={() => setSource(undefined)}>
              <OptionIcon as={Delete} />
            </Option>
          )}
          <Option backgroundColor='#42a5f5' onClick={openDialog}>
            <OptionIcon as={Upload} />
          </Option>
        </Options>
      </Wrapper>
      {loading && (
        <LoadingWrapper>
          <Spinner src='loading.gif' />
        </LoadingWrapper>
      )}
      <input hidden ref={dialogRef} type='file' accept='image/png, image/jpeg' onChange={onSelect} />
    </Container>
  );
};

const asyncRefactorImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      let maxW = 500;
      let maxH = 500;
      let scale = Math.min(maxW / img.width, maxH / img.height);
      if (scale < 1) {
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8);
    };
    img.onerror = () => reject();
    img.src = URL.createObjectURL(file);
  });

const imageUpload = (blob) =>
  new Promise(async (resolve, reject) => {
    const imageId = uuid();
    const ref = storage.ref(`images/${imageId}`);
    ref
      .put(blob)
      .then(() => ref.getDownloadURL())
      .then(() => resolve(imageId))
      .catch(() => reject());
  });

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.loading &&
    css`
      opacity: 0.2;
    `}
`;

const Img = styled.img`
  border-radius: 50%;
  overflow: hidden;
  background-color: black;
  width: 100px;
  height: 100px;
  object-fit: contain;
`;

const NoImage = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: black;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 5px;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  background-color: ${(props) => props.backgroundColor || 'gray'};
  :not(:last-child) {
    margin-bottom: 5px;
  }
`;

const OptionIcon = styled.div`
  fill: ${(props) => props.color || 'white'};
  width: 15px;
  height: 15px;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: not-allowed;
`;

const Spinner = styled.img`
  width: 15px;
  height: 15px;
`;

export default ImageHandler;
